/**
 * Number and bar-geometry helpers for the Health Analytics report.
 *
 * Bar lengths in the design are not raw percentages — they are scaled to a rounded axis maximum.
 * `niceCeil` reproduces that scale exactly: a 39.5% prevalence bar renders 98.7% wide (39.5/40),
 * a 53.9% marker bar renders 98% wide (53.9/55), and the 4,869 column renders 97.4% tall (4869/5000).
 */

export const niceCeil = (max, { percent = true } = {}) => {
  const value = Number(max) || 0;
  return percent
    ? Math.max(5, Math.ceil(value / 5) * 5)
    : Math.max(1000, Math.ceil(value / 1000) * 1000);
};

export const barLength = (value, axisMax) => {
  if (!axisMax) return 0;
  return Math.max(0, Math.min(100, (Number(value) || 0) / axisMax * 100));
};

export const share = (count, total) => (total ? (Number(count) || 0) / total * 100 : 0);

export const fmtPct = (value, digits = 1) => `${(Number(value) || 0).toFixed(digits)}%`;

export const fmtCount = (value) => (Number(value) || 0).toLocaleString('en-US');

export const fmtDecimal = (value, digits = 2) => (Number(value) || 0).toFixed(digits);

/** Bucket counts arrive as [{ flags, count }]; the page addresses them positionally. */
export const bucketCount = (buckets, index) => (buckets && buckets[index] ? buckets[index].count : 0);

export const bucketsTotal = (buckets) =>
  (buckets || []).reduce((sum, bucket) => sum + (bucket.count || 0), 0);

/**
 * Merges the tail of the distribution into a single top bucket.
 *
 * The API always reports four buckets (0 / 1 / 2 / 3+) so one payload serves every chart; chapter 02's
 * age and gender bars show three (zero / one / two-or-more) and collapse here.
 */
export const collapseBuckets = (buckets, max) => {
  const out = Array.from({ length: max + 1 }, (_, index) => ({
    flags: index === max ? `${max}+` : index,
    count: 0,
  }));

  (buckets || []).forEach((bucket) => {
    const flags = typeof bucket.flags === 'number' ? bucket.flags : max;
    out[Math.min(flags, max)].count += bucket.count || 0;
  });

  return out;
};

/** Share of a cohort carrying `from` or more abnormalities. */
export const shareAtLeast = (buckets, from) => {
  const total = bucketsTotal(buckets);
  if (!total) return 0;
  const atLeast = (buckets || []).slice(from).reduce((sum, bucket) => sum + (bucket.count || 0), 0);
  return share(atLeast, total);
};

export const countAtLeast = (buckets, from) =>
  (buckets || []).slice(from).reduce((sum, bucket) => sum + (bucket.count || 0), 0);

/**
 * Segments under about 3% would round away to nothing, so the design floors them at a couple of
 * pixels — 2px inside a stacked bar, 3px for a standalone pill bar.
 */
export const minSegment = (length, floor = 2) => (length > 0 && length < 3 ? `${floor}px` : undefined);

/** Tooltip bodies are HTML, matching the design's `data-tip` innerHTML. */
export const tip = (...lines) => lines.filter(Boolean).join('<br>');

/**
 * How far apart the most and least burdened groups are at two-or-more abnormalities.
 *
 * Used to decide which dimension actually separates a cohort — comparing the gender spread against the
 * age spread — so the page can claim "gender is the fault line" only when the data says so.
 */
export const spreadAtTwoPlus = (groups = []) => {
  const shares = groups.filter((group) => group.total).map((group) => shareAtLeast(group.buckets, 2));
  return shares.length ? Math.max(...shares) - Math.min(...shares) : 0;
};

/**
 * Lowercases a marker label for use mid-sentence while keeping its identifier capitalised:
 * "Vitamin D" → "vitamin D", "Vitamin B12" → "vitamin B12", "Thyroid" → "thyroid".
 * Tokens that are a single letter (optionally with digits) or an acronym stay uppercase.
 */
export const sentenceCaseMarker = (label = '') =>
  String(label)
    .split(' ')
    .map((word) =>
      /^[A-Za-z]\d*$/.test(word) || (word.length > 1 && word === word.toUpperCase())
        ? word.toUpperCase()
        : word.toLowerCase()
    )
    .join(' ');

/** Ranks markers by prevalence so narrative copy can name the leader without hardcoding it. */
export const rankByPrevalence = (markers = []) =>
  [...markers]
    .map((marker) => ({ ...marker, prevalence: share(marker.affected, marker.tested) }))
    .sort((a, b) => b.prevalence - a.prevalence);

/** The design highlights whichever age band carries the highest share at the top bucket. */
export const worstBandLabel = (byAge = []) => {
  let worst = null;
  byAge.forEach((band) => {
    const value = shareAtLeast(band.buckets, band.buckets.length - 1);
    if (band.total && (!worst || value > worst.value)) worst = { band: band.band, value };
  });
  return worst ? worst.band : null;
};
